import { ProductCustomizationResponseDto } from 'client/widget-components/ecom/types/EcomSDK';
import React from 'react';
import styled, { css } from 'styled-components';
import {
    fileLabel,
    fromResponseDto,
    ProductCustomization,
    ProductCustomizationFileAttachment,
    toDisplayValue,
} from '../productCustomization';
import { COLORS, sp } from '../styles';
import { downloadFromUrl, formatCurrency } from '../utils';
import FileFormatIcon from './icons/FileFormatIcon';
import ThemedText from './ThemedText';

/**
 * React port of snipcart-client's product-customization display, used in the
 * order confirmation widget. Mirrors (final SNIP-3904 state):
 *  - `components/cart/items/ItemCustomization.vue` / `.ts`
 *  - `components/cart/items/ItemCustomizationList.vue` / `.ts`
 *
 * In the order view the frontend always passes `show-all-customizations`, so
 * every customization renders expanded (the cart-only collapse button is not
 * part of this widget). Each customization is a pill showing its label, value
 * and — when there is a positive price modifier — a tag with the formatted
 * amount. FILE customizations render one tag per attachment.
 */

/* -------------------------------------------------------------------------- */
/*  Styled primitives                                                         */
/* -------------------------------------------------------------------------- */

// Mirrors `.ecom-item-customization-list`. The top margin matches the
// frontend's `.ecom-line-item__customizations` slot spacing.
const ListWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${sp(0.5)};
    inline-size: 100%;
    margin-block-start: ${sp(1.5)};
`;

// Mirrors `.ecom-item-customization`, filled from the "Dividers & Borders"
// color so the pill reads as part of the same themed surface as every border in
// the widget. It takes a wash of that color rather than the flat value: at full
// strength the themed label and value sat on a surface too close to their own
// color to read (identical to it, with the widget's defaults).
const Pill = styled.div`
    /* Two declarations on purpose: a browser without color-mix() support drops
     * the second and keeps the flat surface. */
    background-color: ${COLORS.border};
    background-color: ${COLORS.customizationSurface};
    padding: ${sp(1)} ${sp(1.5)};
    border-radius: 6px;
    max-inline-size: 100%;
    inline-size: 100%;
    display: flex;
    align-items: baseline;
    gap: ${sp(1)};
    flex-wrap: wrap;
`;

// Mirrors `.ecom-item-customization__content`.
const Content = styled.div`
    flex-grow: 1;
    flex-basis: 200px;
    display: flex;
    align-items: center;
    gap: ${sp(0.5)} ${sp(1)};
    flex-wrap: wrap;
`;

// Mirrors `.ecom-item-customization__label`.
const Label = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
    defaultTag: 'span' as const,
})`
    display: inline;
`;

// Mirrors `.ecom-item-customization__value`.
const Value = styled(ThemedText).attrs({
    textRole: 'secondaryText' as const,
    defaultTag: 'span' as const,
})`
    overflow-wrap: break-word;
`;

// The FILE variant of the value holds one tag per attachment.
const FileTags = styled.span`
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${sp(0.5)};
`;

// Mirrors `.ecom-tag`. `none` = the subtle price-modifier tag, `info` = the
// blue file chips.
const TAG_LEVELS = {
    none: css`
        color: ${COLORS.secondaryText};
        background-color: #ffffff;

        /* The pill behind this chip is a light wash of the same color, so a
         * filled chip would either vanish into it or push its own text back
         * under the contrast the wash just bought. Outlining separates the two
         * and leaves the text on the pill's surface. Guarded so a browser
         * without color-mix() keeps the flat white chip above. */
        @supports (color: color-mix(in srgb, #000, #fff)) {
            background-color: transparent;
            box-shadow: inset 0 0 0 1px ${COLORS.priceModifierBorder};
        }
    `,
    info: css`
        color: ${COLORS.secondaryText};
        background-color: hsl(212deg 85% 90%);
    `,
} as const;

type TagLevel = keyof typeof TAG_LEVELS;

const Tag = styled.button<{ $level: TagLevel; $clickable: boolean }>`
    border: 0;
    border-radius: 6px;
    padding: ${({ $level }) =>
        $level === 'info' ? `${sp(0.5)} ${sp(0.75)}` : sp(0.75)};
    display: inline-flex;
    align-items: center;
    gap: ${sp(0.5)};
    font: inherit;
    ${({ $level }) => TAG_LEVELS[$level]};

    ${({ $clickable }) =>
        $clickable
            ? css`
                  cursor: pointer;
                  transition: color 0.2s ease-in-out;

                  /* tag-hover token = black */
                  &:hover {
                      color: #000000;
                  }
              `
            : css`
                  cursor: default;
              `}

    /* tag-disabled token = black-60 on black-10 */
    &:disabled {
        color: ${COLORS.secondaryText};
        background-color: ${COLORS.mutedFill};
        cursor: default;
    }

    svg {
        flex-shrink: 0;
    }
`;

/* -------------------------------------------------------------------------- */
/*  File download (mirrors ItemCustomization.ts `onFileTagClick`)             */
/* -------------------------------------------------------------------------- */

function downloadFile(file: ProductCustomizationFileAttachment): void {
    if (!file.url) return;
    downloadFromUrl(file.url, file.filename);
}

/* -------------------------------------------------------------------------- */
/*  ItemCustomization                                                         */
/* -------------------------------------------------------------------------- */

interface ItemCustomizationProps {
    customization: ProductCustomization;
    formatMoney: (amount: number) => string;
}

const ItemCustomization: React.FC<ItemCustomizationProps> = ({
    customization,
    formatMoney,
}) => {
    const label = 'label' in customization ? customization.label : undefined;
    const displayValue = toDisplayValue(customization, formatMoney);

    const priceModifier = customization.priceModifier;
    const formattedPriceModifier =
        priceModifier && priceModifier > 0
            ? formatMoney(priceModifier)
            : undefined;

    return (
        <Pill>
            <Content>
                {label && <Label>{label}</Label>}
                {customization.type === 'FILE' ? (
                    <FileTags>
                        {customization.value.map((file) => (
                            <Tag
                                key={file.key}
                                type='button'
                                $level='info'
                                $clickable={!!file.url}
                                disabled={!file.url}
                                onClick={() => downloadFile(file)}
                            >
                                <FileFormatIcon mimetype={file.mimetype} />
                                {fileLabel(file)}
                            </Tag>
                        ))}
                    </FileTags>
                ) : (
                    displayValue && <Value>{displayValue}</Value>
                )}
            </Content>
            {formattedPriceModifier && (
                <Tag as='div' $level='none' $clickable={false}>
                    {formattedPriceModifier}
                </Tag>
            )}
        </Pill>
    );
};

/* -------------------------------------------------------------------------- */
/*  ItemCustomizationList                                                     */
/* -------------------------------------------------------------------------- */

interface ItemCustomizationListProps {
    customizations: ProductCustomizationResponseDto[];
    currency: string;
}

const ItemCustomizationList: React.FC<ItemCustomizationListProps> = ({
    customizations,
    currency,
}) => {
    const formatMoney = React.useCallback(
        (amount: number) => formatCurrency(amount, currency),
        [currency]
    );

    // Map DTOs to display models, dropping the ones with no value to show
    // (mirrors the cart store's use of ProductCustomizationMapper).
    const models = React.useMemo(
        () =>
            customizations
                .map(fromResponseDto)
                .filter((c): c is ProductCustomization => c !== null),
        [customizations]
    );

    if (models.length === 0) return null;

    return (
        <ListWrapper>
            {models.map((customization) => (
                <ItemCustomization
                    key={customization.id}
                    customization={customization}
                    formatMoney={formatMoney}
                />
            ))}
        </ListWrapper>
    );
};

export default ItemCustomizationList;
